import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { toast } from "sonner";
import { api, fmtBRL, fmtInt } from "@/lib/api";
import { apexBase, useChartTheme, PALETTE } from "@/components/production/pcShared";
import { buildBiParams, Panel, Empty } from "./biShared";
import BiInsights from "./BiInsights";

const BADGE = {
  negativo: { label: "NEGATIVO", cls: "bg-red-600 text-white" },
  critico: { label: "CRÍTICO", cls: "bg-red-100 text-red-700 border border-red-400 dark:bg-red-900/30 dark:text-red-400" },
  atencao: { label: "ATENÇÃO", cls: "bg-yellow-100 text-yellow-700 border border-yellow-400 dark:bg-yellow-900/30 dark:text-yellow-400" },
  ok: { label: "OK", cls: "bg-green-100 text-green-700 border border-green-400 dark:bg-green-900/30 dark:text-green-400" },
  sem_giro: { label: "SEM GIRO", cls: "bg-zinc-200 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300" },
};

function StatusBadge({ status }) {
  const b = BADGE[status] || BADGE.ok;
  return <span className={`inline-block text-[9px] font-black px-1.5 py-0.5 rounded whitespace-nowrap ${b.cls}`}>{b.label}</span>;
}

function fmtCoverage(row) {
  if (row.status === "sem_giro") return "∞";
  if (row.coverage_days === null || row.coverage_days === undefined) return "—";
  return `${fmtInt(Math.round(row.coverage_days))}d`;
}

function Kpi({ label, value, sub, tone, testid }) {
  const toneCls = tone === "bad" ? "text-red-600" : tone === "good" ? "text-green-600" : "";
  return (
    <div className="tile p-3" data-testid={testid}>
      <div className="overline text-[9px] text-zinc-500">{label}</div>
      <div className={`text-lg font-black font-mono mt-0.5 ${toneCls}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function CoverageTable({ rows, withColor, testid }) {
  if (!rows?.length) return <Empty />;
  return (
    <div className="overflow-x-auto">
      <table className="table table-compact">
        <thead>
          <tr>
            <th>Tipo</th>
            {withColor && <th>Cor</th>}
            <th className="text-right">Estoque</th>
            <th className="text-right">Vendido no período</th>
            <th className="text-right">Média/dia</th>
            <th className="text-right">Cobertura</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.type}-${r.color || ""}`} data-testid={`${testid}-row-${r.type}${r.color ? `-${r.color}` : ""}`}>
              <td className="font-bold">{r.label}</td>
              {withColor && <td>{r.color}</td>}
              <td className={`text-right font-mono ${r.stock < 0 ? "text-red-600 font-bold" : ""}`}>{fmtInt(r.stock)}</td>
              <td className="text-right font-mono">{fmtInt(r.sold)}</td>
              <td className="text-right font-mono">{fmtInt(Math.round(r.daily))}</td>
              <td className="text-right font-mono font-bold">{fmtCoverage(r)}</td>
              <td><StatusBadge status={r.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BiSupplyTab({ filters }) {
  const dark = useChartTheme();
  const base = apexBase(dark);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!filters.date_from || !filters.date_to || filters.date_from > filters.date_to) return undefined;
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/bi/supply", { params: buildBiParams(filters) })
        .then(({ data: d }) => alive && setData(d))
        .catch(() => alive && toast.error("Falha ao carregar Estoque & Produção"))
        .finally(() => alive && setLoading(false));
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [filters]);

  if (!data) {
    return <div className="tile p-8 text-center text-zinc-400 animate-pulse" data-testid="bi-supply-loading">Carregando estoque & produção...</div>;
  }

  const k = data.kpis;
  const flow = data.daily_flow || [];
  const wip = data.wip_stages || [];
  const balanceTone = k.balance_per_day < 0 ? "bad" : "good";

  return (
    <div className={`space-y-3 ${loading ? "opacity-60 pointer-events-none" : ""}`} data-testid="bi-supply-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2" data-testid="bi-supply-kpis">
        <Kpi label="VENDIDO / DIA" value={`${fmtInt(Math.round(k.sold_per_day))} copos`}
          sub={`${fmtInt(k.sold_cups)} copos em ${k.days}d`} testid="bi-supply-kpi-sold" />
        <Kpi label="EXPEDIDO / DIA" value={`${fmtInt(Math.round(k.shipped_per_day))} copos`}
          sub={`${fmtInt(k.shipped_cups)} copos · ${fmtInt(k.shipped_orders)} pedidos`} testid="bi-supply-kpi-shipped" />
        <Kpi label="BALANÇO / DIA" tone={balanceTone}
          value={`${k.balance_per_day > 0 ? "+" : ""}${fmtInt(Math.round(k.balance_per_day))}`}
          sub={k.balance_per_day < 0 ? "carteira crescendo" : "carteira estável/diminuindo"} testid="bi-supply-kpi-balance" />
        <Kpi label="CARTEIRA EM PRODUÇÃO" value={`${fmtInt(k.wip_cups)} copos`}
          sub={`${fmtInt(k.wip_orders)} pedidos abertos`} testid="bi-supply-kpi-wip" />
        <Kpi label="LEAD TIME MÉDIO" value={k.lead_time_avg === null ? "—" : `${k.lead_time_avg}d`}
          sub={`pedido → expedição (${fmtInt(k.lead_time_n)} pedidos)`} testid="bi-supply-kpi-leadtime" />
        <Kpi label="ESTOQUE CRÍTICO" tone={k.critical_items ? "bad" : "good"} value={fmtInt(k.critical_items)}
          sub={`cobertura < 7 dias${k.negative_items ? ` · ${k.negative_items} negativos` : ""}`} testid="bi-supply-kpi-critical" />
      </div>

      <BiInsights insights={data.insights} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Panel title="ENTRADA (VENDIDO) × SAÍDA (EXPEDIDO) POR DIA — EM COPOS" testid="bi-supply-flow"
          note="Expedição = movimentação do pedido para a etapa Enviado no Kanban.">
          {flow.every((d) => !d.sold && !d.shipped) ? <Empty /> : (
            <Chart type="area" height={260}
              series={[
                { name: "Vendido", data: flow.map((d) => d.sold) },
                { name: "Expedido", data: flow.map((d) => d.shipped) },
              ]}
              options={{
                ...base,
                stroke: { curve: "smooth", width: 2 },
                fill: { type: "gradient", gradient: { opacityFrom: 0.25, opacityTo: 0.02 } },
                xaxis: { categories: flow.map((d) => d.day.slice(5)), tickAmount: 12, labels: { rotate: -45 } },
                yaxis: { labels: { formatter: (v) => fmtInt(v) } },
                tooltip: { ...base.tooltip, y: { formatter: (v) => `${fmtInt(v)} copos` } },
                legend: { fontSize: "10px" },
                colors: [PALETTE[0], PALETTE[2]],
              }} />
          )}
        </Panel>

        <Panel title="CARTEIRA ATUAL POR ETAPA DE PRODUÇÃO" testid="bi-supply-wip"
          note="Foto de AGORA (ignora o filtro de período). Atrasados = data limite de envio já vencida.">
          {wip.every((s) => !s.orders) ? <Empty /> : (
            <div className="overflow-x-auto">
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Etapa</th><th className="text-right">Pedidos</th>
                    <th className="text-right">Copos</th><th className="text-right">Valor Bruto</th>
                    <th className="text-right">Atrasados</th>
                  </tr>
                </thead>
                <tbody>
                  {wip.map((s) => (
                    <tr key={s.status} data-testid={`bi-supply-wip-row-${s.status}`}>
                      <td className="font-bold">{s.label}</td>
                      <td className="text-right font-mono">{fmtInt(s.orders)}</td>
                      <td className="text-right font-mono">{fmtInt(s.cups)}</td>
                      <td className="text-right font-mono">{fmtBRL(s.gross)}</td>
                      <td className={`text-right font-mono ${s.overdue ? "text-red-600 font-bold" : "text-zinc-400"}`}>{fmtInt(s.overdue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <Panel title="COBERTURA DE ESTOQUE POR TIPO — NO RITMO DE VENDAS DO PERÍODO" testid="bi-supply-coverage-types"
        note="Cobertura = estoque atual ÷ média diária de copos vendidos no período filtrado.">
        <CoverageTable rows={data.coverage_types} withColor={false} testid="bi-supply-cov-type" />
      </Panel>

      <Panel title="COBERTURA DE ESTOQUE POR TIPO + COR" testid="bi-supply-coverage-colors">
        <div className="text-[10px] text-zinc-500 border-l-4 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2 rounded mb-2" data-testid="bi-supply-color-note">
          ⚠ O consumo por COR considera apenas os pedidos com cores definidas pela produção (color breakdown).
          Itens sem cores definidas entram só na cobertura por TIPO acima.
        </div>
        <CoverageTable rows={data.coverage} withColor testid="bi-supply-cov-color" />
      </Panel>
    </div>
  );
}
